// seo-schema/org-data.ts
//
// Organization-level facts. SOURCE OF TRUTH: _context/schema-sot/01_Organization.csv
// (edit the CSV/sheet first, then mirror here — provenance lives there, not here).
// Pure data, zero imports. Consumed by jsonld-blocks.ts and llms-manifest.ts.
//
// ✅ VERIFIED 2026-09-05 by Domi, field by field. Every value below is confirmed
// current. Do not re-litigate any of it without a newer confirmation.
//   all 6 sameAs URLs      checked manually, all correct
//   legalName, foundingDate, IATA code, PATA award, address, telephone
//   numberOfEmployees      126, confirmed current
//   email                  booking@flymya.co is CORRECT. Mail uses flymya.co
//                          and the website uses flymya.com. The two differ on
//                          purpose. Do not "fix" the email to flymya.com.
//
// ⚠️ THREE HANDLE SPELLINGS, and all three are real:
//   facebook + linkedin    flymyatravels
//   instagram              flymya
//   tiktok                 flymyatravel   (no "s" on the end)
//
// ⚠️ sameAs ASSERTS IDENTITY. It does not mean "a page about us". It means the
// URL IS this entity. A URL we do not control is a false claim, not a dead
// link. v5 publishes facebook.com/flymya, which lands on web.facebook.com/
// fly.mya/ — a page nobody has confirmed as ours. See A8 in OPEN_ACTIONS.md.
//
// ⚠️ LinkedIn answers 999 to curl. That is its anti-bot code, not a fault.
// Check it in a browser before you call it broken.

export interface PostalAddress {
  streetAddress: string;
  addressLocality: string;
  postalCode?: string;
  addressCountry: string;
}

export interface Membership {
  name: string;
  identifier?: string;
  url?: string;
}

export const ORG = {
  name: "Flymya",
  // Per the site Privacy Policy: "The Company" = the legal owner and operator
  // of flymya.com (confirmed 2026-07-27, schema-sot 01).
  legalName: "Flymya Tech Co., Ltd.",
  url: "https://flymya.com",
  // Served as a real file once Phase 0a ships public/ statics.
  logoPath: "/logo512.png",
  foundingDate: "2015",
  // ✅ REVIEWED BY OPS 2026-08-12 (no longer draft). Reused by llms.txt, the
  // Organization JSON-LD (now in raw HTML, so AI-crawler readable) and About.
  // Ops feedback applied: dropped the "29" payment count (goes stale); we are not
  // online-only, so offline/phone booking and the 24/7 line are stated; office
  // CITIES named in prose while addresses/hours live in structured `location`
  // nodes. Written as four short standalone sentences — who / what / how to book /
  // how to pay — because AI assistants quote this verbatim.
  // ⚠️ Changing this string requires regenerating BOTH llms.txt and
  //    seo-schema/generated/site-graph.json, or they silently diverge.
  description:
    "Flymya is a Myanmar travel agency and booking platform, operating since 2015. Book domestic and international flights, long-distance buses, hotels, Bagan balloon rides, car rentals, tour packages, visas and travel insurance. Booking is available online, by phone with our 24/7 support team, or in person at our ticket counters in Yangon, Mandalay and Taunggyi. Pay with local and international payment methods and receive instant e-tickets.",
  telephone: "+959880441011",
  email: "booking@flymya.co",
  address: {
    streetAddress: "No. 1260, Innwa 3rd Street, Quarter 6, South Okkalapa",
    addressLocality: "Yangon",
    postalCode: "11091",
    addressCountry: "MM",
  } as PostalAddress,
  // schema.org sameAs = "another URL that IS this entity" — official profiles and
  // authoritative references only. NOT a place for press articles that merely
  // mention Flymya; that would be incorrect schema and reads as link spam.
  // Coverage/press belongs in llms.txt's "Press & coverage" section and, above all,
  // in backlinks FROM those articles. See PUBLICATIONS_AND_CITATION_BRIEF.md §2.
  // App-store listings added 2026-08-20 (both verified HTTP 200); they are canonical
  // identity references and are exactly what AI systems use for entity resolution.
  // Still worth adding when they exist: Crunchbase, Wikidata.
  sameAs: [
    "https://www.facebook.com/flymyatravels",
    "https://www.linkedin.com/company/flymyatravels",
    "https://www.instagram.com/flymya",
    "https://www.tiktok.com/@flymyatravel",
    "https://apps.apple.com/us/app/flymya/id1241868739",
    "https://play.google.com/store/apps/details?id=com.flymya",
  ],
  memberOf: [
    { name: "IATA", identifier: "05300013" },
    { name: "PATA", url: "https://www.pata.org/member-listing-industry" },
  ] as Membership[],
  award:
    "PATA Gold Awards 2025 — Best Marketing Campaign, 'Travel Easy, Travel Smart' (Flymya Mini-App in KBZ Pay)",
  numberOfEmployees: 126,
  knowsAbout: [
    "Travel in Myanmar",
    "Travel in South East Asia",
    "Flight tickets",
    "Domestic Myanmar flights",
    "International flights",
    "Hotel booking",
    "Visa services",
    "Car rental",
    "Tours",
  ],
  availableLanguage: ["English", "Burmese"],
  priceRange: "$$",
  messaging: {
    whatsapp: "https://api.whatsapp.com/send/?phone=959797988882",
    viber: "viber://pa?chatURI=flymyatravels",
    messenger: "https://m.me/flymyatravels",
  },
  // Decision 2026-07-23 (PROJECT.md §11): Tim Duits is the only Person in v1.
  leadership: [
    {
      name: "Tim Duits",
      jobTitle: "CEO",
      sameAs: "https://www.linkedin.com/in/timduits/",
    },
  ],
  parentOrganization: {
    name: "Flymya Pte. Ltd.",
    foundingDate: "2015-02-11",
    addressCountry: "SG",
    description:
      "Singapore-incorporated holding company; software and application development and regional corporate functions.",
  },
  subOrganization: {
    name: "Switch.cm",
    description:
      "Property management system and channel manager; official connectivity partner of Booking.com, Expedia and Agoda.",
    // TBD-DOMI (schema-sot 01): public Switch.cm URL.
    url: null as string | null,
  },
  // Aggregated from _context/schema-sot/06_Payments.csv "paymentAccepted" column.
  paymentAccepted:
    "KBZPay, Wave Pay, AYA Pay, CB Pay, Citizens Pay, uabpay, A+ Wallet, OnePay, MPT Pay, MoMoney, MPU card, Visa, Mastercard, Cash (123/NearMe/TrueMoney agents), TrueMoney Wallet (TH)",
} as const;
